import CheckoutPayment from './CheckoutPayment';
import CheckoutRate from './CheckoutRate';
import UserAddress from '../User/UserAddress';
import { intToFormattedString, nullSafeToString } from '../lib/util';

const findAddress = (addresses, candidate) => addresses.find(address => UserAddress.matches(address, candidate));

/**
 * An in-progress checkout. Read the collections to render your form, assign to the writable properties
 * as the shopper makes choices, then hand the checkout back to the
 * {@link Collaterate.Checkout Collaterate.Checkout} save methods.
 *
 * Assignments are validated locally: an address that is not in the corresponding address book, or a
 * shipping service that is not in the current rate list, is rejected with a `console.warn` and left
 * unset rather than being sent to the API and failing there.
 *
 * @property {Project} project - (Read only) The project being checked out.
 * @property {?User} user - (Read only) The signed-in user. Checkout requires one.
 * @property {CheckoutSettings} settings - (Read only) What this storefront permits at checkout.
 * @property {?CheckoutShipment} shipment - (Read only) The shipment as last resolved by the server.
 * @property {CheckoutRate[]} rates - (Read only) The currently available shipping rates.
 * @property {Country[]} countries - (Read only) Countries this project can ship to, for building address forms.
 * @property {State[]} states - (Read only) States for the country this checkout last quoted shipping for. This does **not** follow a country being chosen in an address form - use {@link Collaterate.Checkout.getStatesForCountry getStatesForCountry} for that.
 * @property {UserAddress[]} shippingAddresses - (Read only) The shopper's shipping address book. Kept current by {@link Collaterate.Checkout.addAddress addAddress} and {@link Collaterate.Checkout.refreshAddresses refreshAddresses}.
 * @property {UserAddress[]} billingAddresses - (Read only) The shopper's billing address book. Kept current by {@link Collaterate.Checkout.addAddress addAddress} and {@link Collaterate.Checkout.refreshAddresses refreshAddresses}.
 * @property {OrderMetadataOption[]} orderMetadataOptions - (Read only) Order metadata this storefront collects.
 * @property {SavedPaymentMethod[]} savedPaymentMethods - (Read only) The shopper's vaulted cards and PayPal accounts.
 * @property {number} creditBalance - (Read only) Store credit available to this shopper.
 * @property {string} displayCreditBalance - (Read only) this.creditBalance formatted with commas and two decimal places.
 * @property {string} firstName - The contact first name for this order.
 * @property {string} lastName - The contact last name for this order.
 * @property {string} email - The contact email for this order.
 * @property {string} phone - The contact phone for this order.
 * @property {Object} orderMetadata - Answers to {@link Checkout#orderMetadataOptions orderMetadataOptions}, keyed by option label. Seeded with whatever is already saved on the project, so a resumed checkout is not blank.
 * @property {?number} divisionId - The division this order is placed for, when the storefront uses divisions.
 * @property {?number} clientId - The client this order is attributed to.
 * @property {?UserAddress} shipToAddress - The destination address. Must be one of {@link Checkout#shippingAddresses shippingAddresses}.
 * @property {?UserAddress} shipFromAddress - The origin address for a blind shipment. Must be one of {@link Checkout#shippingAddresses shippingAddresses}.
 * @property {?UserAddress} billToAddress - The billing address. Must be one of {@link Checkout#billingAddresses billingAddresses}.
 * @property {boolean} isBlindShip - Whether to blind ship this order.
 * @property {boolean} isSaturdayDelivery - Whether to request Saturday delivery.
 * @property {boolean} isSignatureOnDelivery - Whether to require a signature on delivery.
 * @property {string} referenceNumber1 - First shipping reference number.
 * @property {string} referenceNumber2 - Second shipping reference number.
 * @property {string} fedExReferenceNumber - FedEx shipping reference number.
 * @property {?CheckoutRate} shippingService - The chosen shipping rate. Must be one of {@link Checkout#rates rates}.
 * @property {string} customShippingDescription - A shopper-described shipping service, used instead of a rate. Only meaningful when {@link CheckoutSettings#customShippingMethodEnabled customShippingMethodEnabled}.
 * @property {boolean} isUSPSService - (Read only) Whether the selected service is the flat-rate USPS option. Derived from {@link Checkout#shippingService shippingService}.
 * @property {?CheckoutPayment} payment - The chosen payment. Set this with {@link Collaterate.Checkout.setPayment Collaterate.Checkout.setPayment}.
 * @property {number} creditApplied - How much store credit to put against this order. Seeded from the credit the server has already applied, so a checkout that arrives with credit on it reports that rather than zero. Cannot exceed the balance or {@link Checkout#orderTotalBeforeCredit orderTotalBeforeCredit}. Set it to 0 to take the credit back off.
 */

class Checkout {
    constructor (data = {}) {
        const project = data.project;
        const shippingAddresses = Object.freeze(data.shippingAddresses || []);
        const billingAddresses = Object.freeze(data.billingAddresses || []);
        const rates = Object.freeze(data.rates || []);
        const creditBalance = data.creditBalance || 0;

        let shipToAddress = null;
        let shipFromAddress = null;
        let billToAddress = null;
        let shippingService = null;
        let payment = null;

        /*
         * Store credit is applied by the server, not by this SDK: ProjectV2Provider#patchProjectWithCreditLimit
         * sets a project's credit limit to the shopper's whole balance the first time the project is
         * patched without one. So a checkout very often starts with credit already on it, and
         * creditApplied has to report that rather than zero. Until the shopper changes it, the value
         * follows the project - which keeps it right across a refresh, when totals can move.
         */
        let creditApplied = 0;
        let isCreditAppliedSet = false;
        const creditOnProject = () => (this.project && this.project.creditAccountsDiscountAmount) || 0;

        const addressSetter = (bookName, label, assign) => function (address) {
            if (!address) {
                return assign(null);
            }

            if (!(address instanceof UserAddress)) {
                console.warn(`Checkout#${label} must be a UserAddress. Not set.`);
                return;
            }

            // Read the book off the instance rather than closing over it, so an address added after
            // this checkout started is accepted.
            if (!findAddress(this[bookName], address)) {
                console.warn(`Checkout#${label} must be an address from Checkout#${bookName}. Add a new one with Collaterate.Checkout.addAddress(). Not set.`);
                return;
            }

            return assign(address);
        };

        Object.defineProperties(this, {
            /*
             * configurable so Collaterate.Checkout.refresh() can swap in a reloaded copy. Still not
             * writable, so a consumer assigning to it throws rather than silently diverging from the
             * server. Without configurable, refresh() threw "Cannot redefine property: project" and
             * took every shipping save down with it, after the patches had already been sent.
             */
            project: { value: project, enumerable: true, configurable: true },
            user: { value: data.user || null, enumerable: true },
            settings: { value: data.settings, enumerable: true },
            shipment: { value: data.shipment || null, enumerable: true, writable: true },
            rates: { value: rates, enumerable: true, writable: true },
            countries: { value: Object.freeze(data.countries || []), enumerable: true, writable: true },
            states: { value: Object.freeze(data.states || []), enumerable: true, writable: true },
            shippingAddresses: { value: shippingAddresses, enumerable: true, writable: true },
            billingAddresses: { value: billingAddresses, enumerable: true, writable: true },
            orderMetadataOptions: { value: Object.freeze(data.orderMetadataOptions || []), enumerable: true },
            savedPaymentMethods: { value: Object.freeze(data.savedPaymentMethods || []), enumerable: true, writable: true },
            creditBalance: { value: creditBalance, enumerable: true },
            displayCreditBalance: { value: intToFormattedString(creditBalance.toFixed(2)), enumerable: true },

            shipToAddress: {
                get: () => shipToAddress,
                set: addressSetter('shippingAddresses', 'shipToAddress', address => shipToAddress = address),
                enumerable: true,
            },
            shipFromAddress: {
                get: () => shipFromAddress,
                set: addressSetter('shippingAddresses', 'shipFromAddress', address => shipFromAddress = address),
                enumerable: true,
            },
            billToAddress: {
                get: () => billToAddress,
                set: addressSetter('billingAddresses', 'billToAddress', address => billToAddress = address),
                enumerable: true,
            },

            shippingService: {
                get: () => shippingService,
                set: function (rate) {
                    if (!rate) {
                        return shippingService = null;
                    }

                    if (!(rate instanceof CheckoutRate)) {
                        console.warn('Checkout#shippingService must be a CheckoutRate. Not set.');
                        return;
                    }

                    if (!this.rates.find(available => available.key === rate.key)) {
                        console.warn('Checkout#shippingService must be one of the currently available rates. Not set.');
                        return;
                    }

                    return shippingService = rate;
                },
                enumerable: true,
            },

            payment: {
                get: () => payment,
                set: newPayment => {
                    if (!newPayment) {
                        return payment = null;
                    }

                    if (!(newPayment instanceof CheckoutPayment)) {
                        console.warn('Checkout#payment must be a CheckoutPayment. Use Collaterate.Checkout.setPayment(). Not set.');
                        return;
                    }

                    return payment = newPayment;
                },
                enumerable: true,
            },

            /*
             * The API validates this against both the credit balance and the order total and rejects an
             * over-application, so it is clamped here to keep the shopper out of a server-side
             * validation error they cannot act on. The ceiling has to be the total *before* credit -
             * ProjectV2Provider#validateCreditLimit adds the applied discount back for exactly this
             * reason. Clamping against the post-credit total would pin a fully covered order at zero and
             * leave the shopper unable to reduce or remove what is already applied.
             */
            creditApplied: {
                get: () => (isCreditAppliedSet ? creditApplied : creditOnProject()),
                set: amount => {
                    const requested = Number(amount) || 0;
                    const ceiling = Math.min(creditBalance, this.orderTotalBeforeCredit);

                    if (requested < 0) {
                        console.warn('Checkout#creditApplied cannot be negative. Not set.');
                        return;
                    }

                    isCreditAppliedSet = true;

                    if (requested > ceiling) {
                        console.warn(`Checkout#creditApplied cannot exceed the credit balance or the order total. Clamped to ${ceiling}.`);
                        return creditApplied = ceiling;
                    }

                    return creditApplied = requested;
                },
                enumerable: true,
            },

            /*
             * Not part of the documented model. The patch builder needs to tell "the shopper chose zero
             * credit" from "the shopper never touched credit": the first has to send creditLimit: 0 to
             * undo the server's automatic application, and the second has to send nothing at all.
             */
            isCreditAppliedSet: {
                get: () => isCreditAppliedSet,
                enumerable: false,
            },
        });

        // Contact details seed from the project, falling back to the user's own details.
        this.firstName = nullSafeToString(project && project.firstName);
        this.lastName = nullSafeToString(project && project.lastName);
        this.email = nullSafeToString(project && project.email);
        this.phone = nullSafeToString(project && project.phone);
        /*
         * Seeded from the project's saved answers, the same way the contact fields are, so a checkout
         * resumed after a reload renders what the shopper already entered. A mutable copy: consumers
         * assign into this, and the project's own copy stays read only.
         *
         * This also stops a save from wiping answers. saveContact always sends the metadata map, so an
         * unseeded form would patch over previously saved values with blanks.
         */
        this.orderMetadata = { ...(project ? project.orderMetadata : {}) };
        this.divisionId = null;
        this.clientId = null;

        this.isBlindShip = false;
        this.isSaturdayDelivery = false;
        this.isSignatureOnDelivery = false;
        this.referenceNumber1 = data.settings ? data.settings.defaultReferenceNumber1 : '';
        this.referenceNumber2 = data.settings ? data.settings.defaultReferenceNumber2 : '';
        this.fedExReferenceNumber = data.settings ? data.settings.defaultFedExReferenceNumber : '';
        this.customShippingDescription = '';
    }

    /*
     * Derived rather than settable: the storefront checkout sets this from the selected service, and
     * letting it be assigned independently would allow the contradictory state of a USPS flag on a UPS
     * selection.
     */
    get isUSPSService () {
        return Boolean(this.shippingService && this.shippingService.isUSPSService);
    }

    /** The order total as the server last calculated it, after any store credit already applied. */
    get orderTotal () {
        return (this.project && this.project.totalPrice) || 0;
    }

    /**
     * The order total before store credit - what credit can be applied against, and so the most that
     * {@link Checkout#creditApplied creditApplied} can be set to. On an order fully covered by credit
     * {@link Checkout#orderTotal orderTotal} is zero while this still reports the real total.
     */
    get orderTotalBeforeCredit () {
        return this.orderTotal + ((this.project && this.project.creditAccountsDiscountAmount) || 0);
    }

    /** Store credit still available after {@link Checkout#creditApplied creditApplied}. */
    get creditRemaining () {
        return this.creditBalance - this.creditApplied;
    }

    /**
     * A local pre-flight check, useful for enabling a submit button. It is not authoritative - the
     * server runs its own validation on submit and may still reject.
     *
     * @returns {boolean}
     */

    get canSubmit () {
        const hasRequiredMetadata = this.orderMetadataOptions
            .filter(option => option.isRequired)
            .every(option => nullSafeToString(this.orderMetadata[option.label]).trim());

        const hasPayment = Boolean(this.payment) && (!this.payment.requiresCard || this.orderTotal === 0
            || Boolean(this.payment.paymentMethodNonce || this.payment.savedPaymentMethodToken));

        return Boolean(this.project && this.project.canCheckout)
            && Boolean(this.user)
            && hasRequiredMetadata
            && hasPayment;
    }
}

export default Checkout;
